import { Zodios, type ZodiosOptions } from '@zodios/core'
import { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

import { API_URL } from '~config'
import { createApiClient as baseCreateApiClient, schemas } from '~interface/zod-schema'
import { setSessionId, setSessionToken } from '~redux/slices/authSlice'
import { store } from '~redux/store'

import { CLIENT_ID, CLIENT_ID_HEADER } from './clientId'
import { csrfToken } from './cookies'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  const baseApi = baseCreateApiClient(baseUrl, options)

  // Create a new Zodios instance with additional configuration
  const api = new Zodios(baseUrl, baseApi.api, {
    ...options,
    axiosConfig: {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  })

  // Apply custom interceptor to the underlying Axios instance
  ;(api.axios as AxiosInstance).interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const {
        userId,
        sessionId: storedSessionId,
        sessionToken: storedSessionToken,
      } = store.getState().auth

      if (csrfToken) {
        config.headers.set('X-CSRFToken', csrfToken)
      }
      config.headers.set(CLIENT_ID_HEADER, CLIENT_ID)
      if (storedSessionId) {
        config.headers.set('X-Session-ID', storedSessionId)
      }
      if (storedSessionToken) {
        config.headers.set('X-Session-Token', storedSessionToken)
      }

      if (config.data instanceof FormData) {
        if (userId) {
          config.data.append('user_id', userId)
        }
      } else if (typeof config.data === 'object' && config.data !== null && userId) {
        config.data = {
          ...config.data,
          user_id: userId,
        }
      }

      return config
    },
    (error) => Promise.reject(error),
  )
  ;(api.axios as AxiosInstance).interceptors.response.use(
    (response: AxiosResponse) => {
      const sessionIdHeader = response.headers['x-session-id']
      const sessionTokenHeader = response.headers['x-session-token']
      if (typeof window !== 'undefined' && window.Office !== undefined) {
        if (sessionIdHeader) {
          store.dispatch(setSessionId(sessionIdHeader))
        }
        if (sessionTokenHeader) {
          store.dispatch(setSessionToken(sessionTokenHeader))
        }
      }
      return response
    },
    (error) => Promise.reject(error),
  )

  return api
}

// Create and export the API instance
export const apiZodios = createApiClient(API_URL || '')

// Re-export schemas
export { schemas }

// Define error handling
export function isErrorResponse(error: unknown): error is Error {
  return error instanceof Error
}
